var __awaiter = this && this.__awaiter || function (thisArg, _arguments, P, generator) {
  function adopt(value) {
    return value instanceof P ? value : new P(function (resolve) {
      resolve(value);
    });
  }
  return new (P || (P = Promise))(function (resolve, reject) {
    function fulfilled(value) {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    }
    function rejected(value) {
      try {
        step(generator["throw"](value));
      } catch (e) {
        reject(e);
      }
    }
    function step(result) {
      result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected);
    }
    step((generator = generator.apply(thisArg, _arguments || [])).next());
  });
};
/* eslint-disable max-lines */
import * as React from 'react';
import { useLocation } from 'react-router-dom';
import { ErrorBoundary } from 'bernie-view';
import { EGDSFloatingButton } from '@egds/react-core/button';
import { EGDSIcon } from '@egds/react-core/icons';
import { EGDSLayoutFlex, EGDSLayoutFlexItem } from '@egds/react-core/layout-flex';
import { EGDSLayoutGrid, EGDSLayoutGridItem, MinMax } from '@egds/react-core/layout-grid';
import { EGDSLayoutPosition } from '@egds/react-core/layout-position';
import { EGDSScrollable } from '@egds/react-core/scrollable';
import { EGDSSpacing } from '@egds/react-core/spacing';
import { Footer, Header } from '@gc/global-components';
import { SharedUiUniversalSearchFormWrapper } from 'src/common/components/common/universal-search-form/universal-search-form-wrapper';
import { UniversalSortFilterWrapper } from 'src/common/components/search/sort-filter/universal-sort-filter';
import { AdsSwappingProvider } from 'src/common/stores/contexts/adsSwapping/ads-swapping-provider';
import { useRefProvider } from 'src/common/stores/contexts/ref-provider';
import { useViewTrackerContext } from 'src/common/stores/contexts/viewTracker';
import { useStores } from 'src/common/stores/use-stores';
import { isBexSplitView } from 'src/common/utils/is-bex-split-view';
import { viewportMap } from 'src/common/utils/viewport-util';
import { ITrackType, useClickstreamAnalytics } from '@shared-ui/clickstream-analytics-context';
import { CustomerNotificationLocation } from '@shared-ui/customer-notifications';
import { useExperiment } from '@shared-ui/experiment-context';
import { OneKeyOnboardingWrapper } from '@shared-ui/retail-loyalty-one-key-onboarding';
import { LodgingQueryParamsProvider } from '@shared-ui/retail-search-tools-lodging';
import { SortAndFiltersViewType } from '@shared-ui/retail-sort-and-filter-lodging';
import { useViewportMap, ViewSizes } from '@shared-ui/viewport-context';
import { CustomerNotifications } from '../common/customer-notifications';
import { ScrolledLazyLoadWrapper } from '../common/lazy/scrolled-lazy-load-wrapper';
import { MesoScripts } from '../meso/internals/meso-scripts';
import { MesoDynamicSideRail } from '../meso/meso-dynamic-side-rail';
import { DynamicSearchMapWrapper } from './map/dynamic-search-map-wrapper';
import { MapPreview } from './map/map-preview';
import { SearchResultsWrapper } from './results/search-results-wrapper';
import { SplitViewResultsHeader } from './split-view-results-header';
import { ResultsWithScrollForAdsSwapping } from './split-view-results-with-scroll-for-ads-swapping';
import { SearchResultsCustomerNotificationWrapper } from './sr-customer-notification-wrapper';
export const dataStids = {
  header: 'header',
  searchForm: 'search-form',
  filterPills: 'filter-pills',
  results: 'results',
  mapTrigger: 'map-trigger',
  map: 'map',
  ad: 'meso-ad',
  customerNotificationsFooter: 'customer-notifications-footer'
};
const MemoizedFooter = React.memo(Footer);
/**
 * given a viewport (amongst other inputs for underlying React components) generate an object that holds references to JSX for each "main"
 * component on the page that has been adjusted for the given sizeClass.
 * @param viewport
 * @param searchResultsScrollRef
 * @param viewTracker
 * @param tracker
 * @returns a SizedSplitViewComponents object keyed by name
 */
const generateSizedSplitViewComponents = (viewport, searchResultsScrollRef, adParentDivRef, viewTracker, tracker, isBEXGSplitViewExperimentEnabled, fullWidthMap, toggleMapWidth, clientPadding, onInit, setBoundsFromSearchResult, expandableMapViewExperimentBucket) => {
  const sizedComponents = {
    header: null,
    searchForm: null,
    filters: null,
    results: null,
    map: null
  };
  sizedComponents.header = React.createElement("div", {
    "data-stid": dataStids.header
  }, React.createElement(Header, {
    fullWidth: true
  }));
  const searchForm = React.createElement("div", null, React.createElement(ErrorBoundary, {
    label: "search-form-error-boundary"
  }, React.createElement(SharedUiUniversalSearchFormWrapper, null)));
  sizedComponents.filters = React.createElement("div", {
    "data-stid": dataStids.filterPills
  }, React.createElement(EGDSSpacing, {
    border: ['blockend']
  }, React.createElement(UniversalSortFilterWrapper, {
    view: SortAndFiltersViewType.FILTER_BAR,
    filterBarSpacing: {
      padding: {
        small: {
          inline: 'three',
          block: 'two'
        },
        medium: {
          inline: 'six',
          block: 'three'
        },
        large: {
          inline: 'six',
          block: 'four'
        }
      }
    }
  })));
  const results =
  // NOTE: visibility is for accessibility requirement
  // If we do not add this logic, it will still be tabbable
  // and from a11y perspective, we do not want to tab something
  // that is not visible on the screen, but present in the DOM
  React.createElement("div", {
    style: fullWidthMap ? {} : {
      visibility: 'hidden'
    },
    "data-stid": "visually-hidden-list"
  }, React.createElement(ErrorBoundary, {
    label: "search-results-error-boundary"
  }, React.createElement(SearchResultsCustomerNotificationWrapper, {
    location: CustomerNotificationLocation.AFTER_HEADER
  }), React.createElement(SplitViewResultsHeader, null), React.createElement(SearchResultsWrapper, null)));
  const trackExpandableMap = isExpand => {
    const eventRefId = isExpand ? 'HOT.SR.map_expand' : 'HOT.SR.map_collapse';
    const eventLinkName = isExpand ? 'Expand Map on search results' : 'Collapse Map on search results';
    tracker.track(ITrackType.EVENT, {
      data: {
        schemaName: 'referrer',
        messageContent: {
          eventRefId: eventRefId,
          linkeventLinkName: eventLinkName,
          eventType: 'eventType'
        }
      },
      config: {
        delay: false
      }
    });
  };
  const expandButtonPosition = isBEXGSplitViewExperimentEnabled || expandableMapViewExperimentBucket === 1 ? {
    top: 'two',
    left: 'two',
    zindex: 'layer3'
  } : {
    top: 'center',
    left: 'two',
    zindex: 'layer3'
  };
  const fullWidthMapTriggerButton = React.createElement(EGDSLayoutPosition, {
    type: "absolute",
    position: expandButtonPosition,
    cloneElement: true
  }, React.createElement(EGDSFloatingButton, {
    name: fullWidthMap ? 'Expand search results map' : 'Collapse search results map',
    "aria-label": fullWidthMap ? 'Expand search results map' : 'Collapse search results map',
    onClick: () => {
      toggleMapWidth();
      trackExpandableMap(fullWidthMap);
    },
    role: "button"
  }, React.createElement(EGDSIcon, {
    name: fullWidthMap ? 'arrow_back_iOS' : 'arrow_forward_iOS'
  })));
  const mapCfg = isBEXGSplitViewExperimentEnabled || expandableMapViewExperimentBucket === 1 ? {
    style: {
      'div:has(> .gm-iv-close)': {
        marginLeft: '66px'
      }
    }
  } : {};
  // NOTE: Two buttons are needed from accessibility perspective,
  // If a user enters a map, they should be able to go to the button
  // and once the user traverse through all the markers, they should be able to
  // move back to the toggle button so they can resize the map to split screen
  const map = React.createElement("aside", {
    "data-stid": dataStids.map
  }, React.createElement(EGDSLayoutPosition, {
    type: "relative",
    style: {
      height: '100%'
    }
  }, (isBEXGSplitViewExperimentEnabled || expandableMapViewExperimentBucket > 0) && fullWidthMapTriggerButton, React.createElement(ErrorBoundary, {
    label: "map-error-boundary"
  }, React.createElement(DynamicSearchMapWrapper, {
    mapFocusHolderDisabled: !fullWidthMap,
    onInit: onInit,
    cfg: mapCfg,
    setBoundsFromSearchResult: setBoundsFromSearchResult,
    clientPadding: clientPadding
  })), (isBEXGSplitViewExperimentEnabled || expandableMapViewExperimentBucket > 0) && fullWidthMapTriggerButton));
  const ad = React.createElement("div", {
    "data-stid": dataStids.ad,
    ref: adParentDivRef
  }, React.createElement(MesoScripts, null), React.createElement(MesoDynamicSideRail, {
    isBEXGSplitViewExperimentEnabled
  }));
  const footerCSR = React.createElement(React.Fragment, null, React.createElement(EGDSSpacing, {
    margin: {
      small: {
        inline: 'three'
      },
      large: {
        inline: 'six'
      }
    }
  }, React.createElement("div", {
    "data-stid": dataStids.customerNotificationsFooter
  }, React.createElement(CustomerNotifications, {
    location: CustomerNotificationLocation.FOOTER
  }))), React.createElement(ErrorBoundary, {
    label: "lodging.splitview.footer"
  }, React.createElement(ScrolledLazyLoadWrapper, null, React.createElement(MemoizedFooter, {
    skipSsr: true
  }))));
  const resultsWithFooter = React.createElement(React.Fragment, null, React.createElement("div", {
    "data-stid": dataStids.results,
    ref: searchResultsScrollRef
  }, React.createElement(EGDSSpacing, {
    padding: {
      inline: 'six',
      blockstart: 'three'
    }
  }, results)), footerCSR);
  if (viewport === ViewSizes.SMALL || viewport === ViewSizes.MEDIUM) {
    sizedComponents.searchForm = React.createElement("div", {
      "data-stid": dataStids.searchForm
    }, React.createElement(EGDSSpacing, {
      margin: {
        small: {
          inline: 'three',
          blockstart: 'three'
        },
        medium: {
          inline: 'six',
          blockstart: 'three'
        }
      }
    }, searchForm));
    sizedComponents.results = React.createElement("div", {
      "data-stid": dataStids.results
    }, React.createElement(EGDSSpacing, {
      padding: {
        small: {
          inline: 'three'
        },
        medium: {
          inline: 'six'
        }
      }
    }, results), React.createElement("div", {
      "data-stid": dataStids.mapTrigger
    }, React.createElement(MapPreview, {
      viewTracker: viewTracker,
      tracker: tracker,
      dialogView: SortAndFiltersViewType.FILTER_BAR
    })), footerCSR);
  } else {
    sizedComponents.searchForm = React.createElement("div", {
      "data-stid": dataStids.searchForm
    }, React.createElement(EGDSLayoutFlex, null, React.createElement(EGDSLayoutFlexItem, {
      grow: 1,
      maxWidth: 1200
    }, React.createElement(EGDSSpacing, {
      margin: {
        inline: 'six',
        blockstart: 'four'
      }
    }, searchForm))));
    sizedComponents.results = React.createElement(EGDSScrollable, {
      direction: 'vertical'
    }, resultsWithFooter);
    sizedComponents.map = map;
  }
  if (viewport === ViewSizes.EXTRA_LARGE) {
    if (isBEXGSplitViewExperimentEnabled) {
      sizedComponents.results = React.createElement(ResultsWithScrollForAdsSwapping, null, resultsWithFooter);
    }
    sizedComponents.map = React.createElement(EGDSLayoutFlex, {
      space: "six",
      style: {
        overflowY: 'hidden'
      }
    }, React.createElement(EGDSLayoutFlexItem, {
      minWidth: 336,
      grow: 1
    }, map), React.createElement(EGDSLayoutFlexItem, {
      minWidth: 160,
      maxWidth: 160
    }, ad));
  }
  return sizedComponents;
};
export const SplitView = () => {
  const [searchResultsListRef, adParentDivRef] = useRefProvider('searchResultsList', 'ad-parent-div');
  const viewTracker = useViewTrackerContext();
  const tracker = useClickstreamAnalytics();
  const mapRef = React.useRef();
  const viewport = useViewportMap(viewportMap);
  const {
    context,
    experiment
  } = useStores();
  const isBEXGSplitViewExperimentEnabled = isBexSplitView(context, experiment);
  const {
    exposure: expandableMapViewOnSRPExposure
  } = useExperiment('Expandable_Map_View_on_SRP_Web');
  const [fullWidthMap, setFullWidthMap] = React.useState(false);
  const internalOnInit = React.useCallback(map => {
    mapRef.current = map;
  }, []);
  const [boundsFromFullscreenMap, setBoundsFromFullscreenMap] = React.useState();
  const setBoundsFromSearchResult = React.useCallback(bounds => {
    if (bounds) {
      const {
        northeast: {
          latitude: neLat,
          longitude: neLng
        },
        southwest: {
          latitude: swLat,
          longitude: swLng
        }
      } = bounds;
      const mapBounds = {
        sw: [swLat, swLng],
        ne: [neLat, neLng]
      };
      setBoundsFromFullscreenMap(mapBounds);
    } else {
      setBoundsFromFullscreenMap(undefined);
    }
  }, [fullWidthMap, setBoundsFromFullscreenMap]);
  const clientPadding = undefined;
  // exposing and/or utilizing the mapRef directly in shopping-pwa is not recommended and will be adjusted in the future. Please think twice before replicating this type of implementation.
  const toggleMapWidth = React.useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
    var _a;
    setFullWidthMap(val => !val);
    const features = (_a = mapRef.current) === null || _a === void 0 ? void 0 : _a.listFeatures();
    if (features && (features === null || features === void 0 ? void 0 : features.length)) {
      const delay = ms => new Promise(resolve => setTimeout(resolve, ms));
      yield delay(500);
      const map = mapRef.current;
      if (map && boundsFromFullscreenMap) {
        yield map.fitBounds(boundsFromFullscreenMap.sw, boundsFromFullscreenMap.ne, undefined, clientPadding);
      }
    }
  }), [boundsFromFullscreenMap, setFullWidthMap, clientPadding]);
  const sizedComponents = generateSizedSplitViewComponents(viewport, searchResultsListRef, adParentDivRef, viewTracker, tracker, isBEXGSplitViewExperimentEnabled, fullWidthMap === false, toggleMapWidth, clientPadding, internalOnInit, setBoundsFromSearchResult, expandableMapViewOnSRPExposure.bucket);
  return React.createElement(AdsSwappingProvider, null, React.createElement(LodgingQueryParamsProvider, {
    urlSearchParams: new URLSearchParams(useLocation().search)
  }, React.createElement(EGDSLayoutGrid, {
    columns: {
      large: fullWidthMap ? ['0px', '1fr'] : [MinMax('660px', '800px'), MinMax('332px', '400px')],
      /* eslint-disable camelcase */
      extra_large: fullWidthMap ? ['0px', '1fr'] : [MinMax('680px', '864px'), MinMax('520px', '1fr')]
    },
    blockSize: viewport === ViewSizes.EXTRA_LARGE || viewport === ViewSizes.LARGE ? '100vb' : 'full_size'
  }, React.createElement(EGDSLayoutGridItem, {
    colSpan: 'all'
  }, sizedComponents.header), React.createElement(EGDSLayoutGridItem, {
    colSpan: 'all'
  }, sizedComponents.searchForm), React.createElement(EGDSLayoutGridItem, {
    colSpan: 'all'
  }, sizedComponents.filters), React.createElement(EGDSLayoutGridItem, null, sizedComponents.results), React.createElement(EGDSLayoutGridItem, null, sizedComponents.map)), React.createElement(ErrorBoundary, {
    label: "onekey.onboarding.shopping-pwa"
  }, React.createElement(OneKeyOnboardingWrapper, null))));
};
SplitView.displayName = 'SplitView';
export default SplitView;